// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Item.tsx
 * @input Uses React, ReactNode, StyleXStyles, theme tokens, useClickableContainer
 * @output Exports Item component, ItemProps type
 * @position Core layout primitive; consumed by index.ts, tested by Item.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Item/Item.doc.mjs
 * - /packages/core/src/Item/Item.test.tsx
 * - /packages/core/src/Item/index.ts
 * - /apps/storybook/stories/Item.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Item/ (showcase blocks)
 */
import { useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, radiusVars, spacingVars, durationVars, easeVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { computeTargetAndRel } from "../Link/computeTargetAndRel.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { useClickableContainer } from "../hooks/useClickableContainer.js";
import { useDevWarning } from "../hooks/useDevWarning.js";
import { themeProps } from "../utils/themeProps.js";
import { focusOutlineProps } from "../utils/focusOutline.stylex.js";

// =============================================================================
// Types
// =============================================================================
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Constants
// =============================================================================

/**
 * Roles on which WAI-ARIA permits the aria-selected attribute.
 * https://www.w3.org/TR/wai-aria-1.2/#aria-selected
 */
const ARIA_SELECTED_ROLES = new Set(['option', 'tab', 'row', 'gridcell', 'columnheader', 'rowheader', 'treeitem']);

// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kg3NbH: "xf314gf",
    kVAEAm: "x1n2onr6",
    kB7OPa: "x9f619",
    k9WMMc: "x1yc453h",
    kaIpWk: "xh6dtrn",
    $$css: true
  },
  alignStart: {
    kGNEyG: "x1cy8zhl",
    $$css: true
  },
  interactive: {
    kkrTdU: "x1ypdohk",
    k1ekBW: "x15406qy",
    kIyJzY: "xkvfbh3",
    kAMwcw: "xlr8y92",
    kWkggS: "xjbqb8w xe9uy6x xyxi2l3",
    $$css: true
  },
  highlighted: {
    kWkggS: "x1lmrjuc",
    $$css: true
  },
  selected: {
    kWkggS: "xgcxg3y",
    $$css: true
  },
  disabled: {
    kkrTdU: "x1h6gzvc",
    kfzvcC: "x47corl",
    $$css: true
  },
  label: {
    kMwMTN: "x5tbw38",
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    $$css: true
  },
  labelSingleTruncate: {
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  labelMultiTruncate: {
    kVQacm: "xb3r6kr",
    k1xSpc: "x104kibb",
    kgKLqz: "x1ua5tub",
    $$css: true
  },
  description: {
    kMwMTN: "x1gb3s7i",
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    $$css: true
  },
  descriptionSingleTruncate: {
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  descriptionMultiTruncate: {
    kVQacm: "xb3r6kr",
    k1xSpc: "x104kibb",
    kgKLqz: "x1ua5tub",
    $$css: true
  }
};
const dynamicStyles = {
  lineClamp: lines => [{
    kJFfOR: lines != null ? "x1yhjpo9" : lines,
    $$css: true
  }, {
    "--x-WebkitLineClamp": lines != null ? lines : undefined
  }]
};
const densityStyles = {
  compact: {
    k8WAf4: "xu0wf1k",
    $$css: true
  },
  balanced: {
    k8WAf4: "xce4md1",
    $$css: true
  },
  spacious: {
    k8WAf4: "x8o8v82",
    kg3NbH: "xrrkdod",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * A universal item primitive that unifies the "start content + label +
 * description + end content" layout pattern. Use as a building block for list items,
 * menu items, contact rows, notification items, and more.
 *
 * @example
 * ```
 * <Item
 *   startContent={<Avatar src={user.avatar} size="sm" />}
 *   label={user.name}
 *   description={user.role}
 *   endContent={<Badge>Admin</Badge>}
 *   onClick={() => navigate(`/users/${user.id}`)}
 * />
 * ```
 */
export function Item({
  as: Component = 'div',
  marker,
  startContent,
  label,
  description,
  endContent,
  align = 'center',
  density = 'balanced',
  labelLines,
  descriptionLines,
  onClick,
  interactiveRef,
  href,
  target: targetFromProps,
  rel: relFromProps,
  isHighlighted = false,
  isSelected = false,
  isDisabled = false,
  xstyle,
  className,
  style,
  ref,
  role,
  ...restProps
}) {
  const LinkComponent = useLinkComponent();

  // Delegation mode: the row is an enlarged click/tap target for a nested
  // control (e.g. a checkbox) that owns the keyboard access and action. The
  // control is the row's only tab stop; the row proxies surface clicks to it.
  const isDelegate = interactiveRef != null;
  const containerRef = useRef(null);
  // Only onClick is needed: onMouseUp handles middle-click href navigation,
  // which delegation mode never has (href is ignored here).
  const {
    onClick: delegatedOnClick
  } = useClickableContainer({
    containerRef,
    interactiveRef: interactiveRef ?? undefined,
    disabled: isDisabled
  });
  useDevWarning('Item', '`interactiveRef` is mutually exclusive with `onClick`/`href`. In ' + 'delegation mode the row only forwards clicks to the referenced control, ' + 'so `onClick`/`href` are ignored. Drop one of them.', isDelegate && (onClick != null || href != null));
  const isInteractive = onClick != null || href != null || isDelegate;
  const {
    target,
    rel
  } = computeTargetAndRel(targetFromProps, relFromProps);
  // When a semantic role is provided (e.g. "menuitem"), a parent component
  // handles keyboard access. Skip the invisible button/anchor and put
  // onClick directly on the root element instead.
  const hasParentRole = role != null;
  // aria-selected is only valid on selectable roles (option, tab, treeitem,
  // grid cells). On the default div/li root the attribute is invalid ARIA
  // (axe: aria-allowed-attr), so selection stays visual-only there — callers
  // that need selection semantics pass a permitted role.
  const allowsAriaSelected = role != null && ARIA_SELECTED_ROLES.has(role);
  const isStringLabel = typeof label === 'string';
  const isStringDescription = typeof description === 'string';
  const labelTruncateStyle = labelLines != null ? labelLines === 1 ? styles.labelSingleTruncate : styles.labelMultiTruncate : isStringLabel ? styles.labelSingleTruncate : null;
  const descriptionTruncateStyle = descriptionLines != null ? descriptionLines === 1 ? styles.descriptionSingleTruncate : styles.descriptionMultiTruncate : isStringDescription ? styles.descriptionSingleTruncate : null;
  const labelAndDescription = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      ...stylex.props(styles.label, labelTruncateStyle, labelLines != null && labelLines > 1 && dynamicStyles.lineClamp(labelLines)),
      children: label
    }), description != null && /*#__PURE__*/_jsx("span", {
      ...stylex.props(styles.description, descriptionTruncateStyle, descriptionLines != null && descriptionLines > 1 && dynamicStyles.lineClamp(descriptionLines)),
      children: description
    })]
  });
  const handleContainerClick = e => {
    if (isDisabled) {
      return;
    }
    const target = e.target;
    if (target.closest('button, a, input, select, textarea')) {
      return;
    }
    onClick?.(e);
  };
  const innerContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [marker, startContent != null && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x3psx0u x78zum5"
      },
      children: startContent
    }), hasParentRole || isDelegate ?
    /*#__PURE__*/
    // Delegation mode (and parent-role mode) put the label in a plain span:
    // keyboard access lives on the nested control, so no invisible
    // button/anchor is rendered and the row adds no second tab stop.
    _jsx("span", {
      ...{
        0: {
          className: "x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h"
        },
        1: {
          className: "x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h xbyyjgo"
        }
      }[!!isDisabled << 0],
      children: labelAndDescription
    }) : href != null ? /*#__PURE__*/_jsx(LinkComponent, {
      href: href,
      target: target,
      rel: rel,
      "aria-disabled": isDisabled || undefined,
      tabIndex: isDisabled ? -1 : undefined,
      ...{
        0: {
          className: "xmper1u xln7xf2 x1heor9g x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h x1hl2dhg x1a2a7pz"
        },
        1: {
          className: "xmper1u xln7xf2 x1heor9g x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h x1hl2dhg x1a2a7pz xbyyjgo"
        }
      }[!!isDisabled << 0],
      children: labelAndDescription
    }) : onClick != null ? /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: onClick,
      disabled: isDisabled,
      ...{
        0: {
          className: "xmper1u xln7xf2 x1heor9g x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h x1a2a7pz"
        },
        1: {
          className: "xmper1u xln7xf2 x1heor9g x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h x1a2a7pz xbyyjgo"
        }
      }[!!isDisabled << 0],
      children: labelAndDescription
    }) : /*#__PURE__*/_jsx("span", {
      ...{
        0: {
          className: "x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h"
        },
        1: {
          className: "x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h xbyyjgo"
        }
      }[!!isDisabled << 0],
      children: labelAndDescription
    }), endContent != null && /*#__PURE__*/_jsx("span", {
      ...{
        0: {
          className: "x3psx0u x78zum5 xvc5jky"
        },
        1: {
          className: "x3psx0u x78zum5 xvc5jky xbyyjgo"
        }
      }[!!isDisabled << 0],
      children: endContent
    })]
  });
  return /*#__PURE__*/_jsx(Component, {
    ref: isDelegate ? mergeRefs(ref, containerRef) : ref,
    ...restProps,
    "aria-selected": allowsAriaSelected && isSelected || undefined
    // aria-selected is invalid on roles that don't permit it (listitem, a
    // bare div, etc.). For those, convey selection via aria-current — valid
    // on any element — so the state still reaches AT. Written after
    // {...restProps} so it must defer to a consumer-provided aria-current.
    ,
    "aria-current": restProps['aria-current'] ?? (isSelected && !allowsAriaSelected ? true : undefined),
    "aria-disabled": isDisabled || undefined,
    ...mergeProps(themeProps('item', {
      density,
      align
    }), focusOutlineProps.focusWithin(styles.root, densityStyles[density], align === 'start' && styles.alignStart, isInteractive && styles.interactive, isHighlighted && styles.highlighted, isSelected && styles.selected, isDisabled && !hasParentRole && styles.disabled, xstyle), className, style),
    role: role,
    onClick: isDelegate ? delegatedOnClick : hasParentRole ? onClick : isInteractive ? handleContainerClick : undefined,
    children: innerContent
  });
}
Item.displayName = 'Item';